import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, CircleDot, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ApiError, api } from "../../api";
import { runBatched } from "../../lib/selection";
import type { IssuesMeta, IssueView } from "../../types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Kbd } from "../ui/kbd";
import { Separator } from "../ui/separator";
import { useSidebar } from "../ui/sidebar";
import { Spinner } from "../ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { StatusDisc, TypeIcon } from "./meta";
import { Picker } from "./picker";

export type BulkAction =
  | { kind: "status"; statusKey: string }
  | { kind: "type"; typeKey: string }
  | { kind: "archive"; archived: boolean };

/** How many issue requests to keep in flight at once. */
const CONCURRENCY = 6;

/** Skip issues the action would leave untouched, so we do not log no-op activity. */
function needsChange(issue: IssueView, action: BulkAction): boolean {
  switch (action.kind) {
    case "status":
      return issue.statusKey !== action.statusKey;
    case "type":
      return issue.typeKey !== action.typeKey;
    case "archive":
      return Boolean(issue.archivedAt) !== action.archived;
  }
}

function applyAction(issue: IssueView, action: BulkAction) {
  switch (action.kind) {
    case "status":
      return api.updateIssue(issue.identifier, { statusKey: action.statusKey });
    case "type":
      return api.updateIssue(issue.identifier, { typeKey: action.typeKey });
    case "archive":
      return api.setIssueArchived(issue.identifier, action.archived);
  }
}

function describe(action: BulkAction, count: number, meta: IssuesMeta | undefined): string {
  const noun = count === 1 ? "issue" : "issues";
  switch (action.kind) {
    case "status": {
      const name = meta?.statuses.find((s) => s.key === action.statusKey)?.name ?? action.statusKey;
      return `Moved ${count} ${noun} to ${name}`;
    }
    case "type": {
      const name = meta?.types.find((t) => t.key === action.typeKey)?.name ?? action.typeKey;
      return `Changed ${count} ${noun} to ${name}`;
    }
    case "archive":
      return `${action.archived ? "Archived" : "Restored"} ${count} ${noun}`;
  }
}

/**
 * Applies one action to many issues through the existing per-issue endpoints, so
 * activity, notifications and factory hooks behave exactly as single edits do.
 * Resolves with the ids that failed so the caller can keep them selected for a retry.
 */
export function useBulkIssueActions(meta: IssuesMeta | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ issues, action }: { issues: IssueView[]; action: BulkAction }) => {
      const targets = issues.filter((issue) => needsChange(issue, action));
      const results = await runBatched(targets, CONCURRENCY, (issue) => applyAction(issue, action));
      const failed: Array<{ issue: IssueView; reason: unknown }> = [];
      results.forEach((result, index) => {
        if (result.status === "rejected") failed.push({ issue: targets[index]!, reason: result.reason });
      });
      return { action, targets, failed };
    },
    onSuccess: ({ action, targets, failed }) => {
      void queryClient.invalidateQueries({ queryKey: ["issues"] });
      for (const issue of targets) {
        void queryClient.invalidateQueries({ queryKey: ["issue", issue.identifier] });
      }
      if (targets.length === 0) {
        toast.info("Nothing to change");
        return;
      }
      if (failed.length === 0) {
        toast.success(describe(action, targets.length, meta));
        return;
      }
      const first = failed[0]!.reason;
      toast.error(`${failed.length} of ${targets.length} could not be updated`, {
        description: first instanceof ApiError ? first.message : undefined,
      });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Bulk update failed"),
  });
}

/** The single value shared by every issue, or none when they disagree. */
function common(issues: IssueView[], pick: (issue: IssueView) => string): string[] {
  const first = issues[0] ? pick(issues[0]) : undefined;
  return first !== undefined && issues.every((issue) => pick(issue) === first) ? [first] : [];
}

/**
 * Floating toolbar pinned to the bottom of the viewport while issues are selected.
 * Rendered inside the page so it inherits the sidebar width and stays centred over
 * the content column rather than the whole window.
 */
export function BulkActionBar({
  issues,
  total,
  meta,
  pending,
  onAction,
  onSelectAll,
  onClear,
}: {
  issues: IssueView[];
  /** Everything currently listed, for the "select all" affordance. */
  total: number;
  meta: IssuesMeta | undefined;
  pending: boolean;
  onAction: (action: BulkAction) => void;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const { state, isMobile } = useSidebar();
  if (issues.length === 0) return null;
  const count = issues.length;
  const noun = count === 1 ? "issue" : "issues";
  const allArchived = issues.every((issue) => issue.archivedAt);

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-[calc(1.25rem+var(--tab-bar-h))] z-40 flex justify-center px-4",
        !isMobile && state === "expanded" && "md:left-(--sidebar-width)",
      )}
    >
      <div
        role="toolbar"
        aria-label="Bulk actions"
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1 rounded-xl border bg-popover/95 p-1.5 text-popover-foreground shadow-lg backdrop-blur animate-pop-up supports-[backdrop-filter]:bg-popover/80 motion-reduce:animate-none"
      >
        <span className="flex items-center gap-2 px-2 text-sm font-medium tabular-nums">
          {pending && <Spinner className="size-3.5" />}
          {count} selected
        </span>
        {count < total && (
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onSelectAll}>
            Select all {total}
          </Button>
        )}
        <Separator orientation="vertical" className="mx-1 h-5 self-center" />

        <Picker
          multiple={false}
          align="center"
          placeholder="Change status…"
          options={(meta?.statuses ?? []).map((status) => ({
            value: status.key,
            label: status.name,
            icon: <StatusDisc category={status.category} color={status.color} />,
          }))}
          selected={common(issues, (issue) => issue.statusKey)}
          onToggle={(statusKey) => onAction({ kind: "status", statusKey })}
        >
          <Button variant="ghost" size="sm" disabled={pending || !meta}>
            <CircleDot /> Status
          </Button>
        </Picker>

        <Picker
          multiple={false}
          align="center"
          placeholder="Change type…"
          options={(meta?.types ?? []).map((type) => ({
            value: type.key,
            label: type.name,
            icon: <TypeIcon typeKey={type.key} color={type.color} />,
          }))}
          selected={common(issues, (issue) => issue.typeKey)}
          onToggle={(typeKey) => onAction({ kind: "type", typeKey })}
        >
          <Button variant="ghost" size="sm" disabled={pending || !meta}>
            <Tag /> Type
          </Button>
        </Picker>

        {allArchived ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => onAction({ kind: "archive", archived: false })}
          >
            <ArchiveRestore /> Restore
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={pending}>
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Archive {count} {noun}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {issues.some((issue) => issue.subIssues.total > 0)
                    ? "They leave the default lists and keep their status. Sub-issues are archived and restored with them."
                    : "They leave the default lists and keep their status. You can restore them from the Archived tab."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onAction({ kind: "archive", archived: true })}>
                  Archive {count} {noun}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        <Separator orientation="vertical" className="mx-1 h-5 self-center" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Clear selection" onClick={onClear}>
              <X />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-1.5">
            Clear selection <Kbd>Esc</Kbd>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
